import * as yup from "yup";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import React, { useState, useEffect } from "react";

import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import LoadingButton from "@mui/lab/LoadingButton";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { Box, Button, TextareaAutosize } from "@mui/material";

import Title from "../../../common/Title";

import useAlert from "../../../../hooks/useAlert";
import useAxios from "../../../../hooks/useAxios";
import { __clearStatusTmobilej } from "../../../../api/sim";

const ClearStatusForm = () => {
  const [rawData, setRawData] = useState("");
  const [linesData, setLinesData] = useState([]);
  const [formData, setFormData] = useState(null);

  const { setAlertMessage } = useAlert();
  const nav = useNavigate();

  const [response, call, loading] = useAxios({ ...__clearStatusTmobilej });

  const lineValidationSchema = yup.object({
    iccid: yup
      .string("iccid")
      .min(19, "Should be exactly 19 or 20 digits long")
      .max(20, "Should be exactly 19 or 20 digits long")
      .required(),
  });

  useEffect(() => {
    if (formData) {
      call({
        data: {
          lines: [...linesData],
        },
      });
    }
  }, [formData]);

  useEffect(() => {
    if (!response) return;
    if (response.status) {
      setAlertMessage({ type: "success", message: "Clear status request queued" });
      setLinesData([]);
      nav("/tmobilej/clear_status");
    }
    formik.setSubmitting(false);
  }, [response]);

  const validateFunction = (data) => {
    const errors = {};
    const formattedLines = [];

    if (!data) {
      return errors;
    }

    const lines = data.split("\n");
    lines.some((line, idx) => {
      const trimmed = line.trim();
      if (!trimmed) {
        return false;
      }

      try {
        const obtainedData = { iccid: trimmed.split(",")[0].trim() };
        lineValidationSchema.validateSync(obtainedData);

        if (isNaN(obtainedData.iccid)) {
          throw new Error("Invalid Number");
        }
        formattedLines.push(obtainedData);
      } catch (err) {
        errors.lines = `Invalid format in line ${idx + 1}: ${line}`;
        return true;
      }

      return false;
    });

    if (!errors.lines) {
      setLinesData(formattedLines);
    }

    return errors;
  };

  const formik = useFormik({
    initialValues: { lines: [] },
    onSubmit: () => {
      setFormData(true);
    },
    validateOnChange: true,
  });

  const handleParse = (inputFile) => {
    const reader = new FileReader();

    reader.onload = async ({ target }) => {
      setRawData(target.result);
      formik.setErrors(validateFunction(target.result));
    };
    reader.readAsText(inputFile);
  };

  const handleFileChange = (e) => {
    if (!e.target.files.length) return;
    const inputFile = e.target.files[0];
    const fileExtension = inputFile?.type.split("/")[1];
    if (fileExtension !== "csv") {
      formik.setErrors({
        ...formik.errors,
        upload: "Please input a csv file",
      });
      return;
    }
    handleParse(inputFile);
  };

  if (loading) {
    return <Title content="Please wait..." variant="subtitle2" gutterBottom />;
  }

  return (
    <Box
      sx={{ width: "100%" }}
      display="flex"
      alignItems="center"
      flexDirection="column"
    >
      <Title
        variant="h5"
        content="Clear Status Request"
        sx={{ mb: 3 }}
        gutterBottom
      />
      <Grid
        container
        spacing={2}
        direction="column"
        justifyContent="center"
        alignItems="center"
      >
        <Grid item xs={12}>
          <FormControl
            error={Boolean(formik.errors.upload)}
            sx={{ width: "100%" }}
          >
            <Button
              component="label"
              variant="outlined"
              startIcon={<UploadFileIcon />}
              sx={{ marginRight: "1rem" }}
            >
              Upload CSV
              <input
                type="file"
                accept=".csv"
                hidden
                onChange={handleFileChange}
              />
            </Button>
            {formik.errors.upload ? (
              <FormHelperText id="upload-error-text">
                {formik.errors.upload}
              </FormHelperText>
            ) : null}
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <InputLabel htmlFor="lines">
            Add one ICCID per line (19–20 digits)
          </InputLabel>
          <FormControl
            error={Boolean(formik.errors.lines)}
            sx={{ width: "100%" }}
          >
            <TextareaAutosize
              rows={6}
              aria-label="minimum height"
              placeholder={`ICCID\nICCID`}
              style={{ minWidth: 550 }}
              name="lines"
              minRows={5}
              id="lines"
              value={rawData}
              onChange={(e) => {
                const data = e.target.value;
                setRawData(data);
                formik.setErrors(validateFunction(data));
              }}
            />
            {formik.errors.lines ? (
              <FormHelperText id="lines-error-text">
                {formik.errors.lines}
              </FormHelperText>
            ) : null}
          </FormControl>
        </Grid>

        <Grid item>
          <LoadingButton
            disabled={formik.errors.lines || !linesData.length}
            onClick={() =>
              !loading && !formik.isSubmitting && formik.submitForm()
            }
            size="large"
            loading={loading}
            variant="contained"
          >
            Submit
          </LoadingButton>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ClearStatusForm;
